/* eslint-disable promise/prefer-await-to-callbacks */
import { combineEpics, Epic, ofType } from 'redux-observable';
import { from, of, timer } from 'rxjs';
import { catchError, delayWhen, pluck, switchMap } from 'rxjs/operators';
import { api } from 'utils/axiosInstance';
import { apiErrorHandler, notify } from 'utils/handlers/notificationHandler';
import { clearToken } from 'utils/handlers/tokenHandler';
import i18n from 'utils/initTranslation';
import { AuthenticationResult } from 'utils/restApplicationClient';
import { kenticoSdk } from 'utils/services/kenticoSdk.service';
import {
  chooseKenticoProject,
  forgotPassword,
  getKenticoProject,
  getKenticoProjectError,
  getProjectPrefix,
  getProjectPrefixError,
  getProjectPrefixSuccess,
  getUserConfig,
  getUserConfigError,
  getUserConfigSuccess,
  login,
  loginError,
  loginSuccess,
  logoutUser,
  logoutUserSuccess,
  refreshToken,
  refreshTokenSuccess,
  resetPassword,
  resetPasswordError,
  resetPasswordSuccess,
  verifyResetPasswordToken,
  verifyResetPasswordTokenError,
  verifyResetPasswordTokenSuccess,
} from './auth.actions';

const loginEpic: Epic = (action$) =>
  action$.pipe(
    ofType(login.type),
    switchMap(({ payload }) =>
      from(api.authenticateUser(payload)).pipe(
        switchMap(({ data }) => {
          return kenticoSdk?.context?.projectId ? of(getKenticoProject(data)) : of(loginSuccess(data));
        }),
        catchError((error) => {
          if (error.response?.data?.errorCode) {
            return of(
              loginError({
                key: 'login',
                message: error.response.data.errorCode,
              }),
            );
          } else {
            return of(loginError({ key: 'login', message: 'errormessage.apiUrlError' }));
          }
        }),
      ),
    ),
  );

const refreshTokenEpic: Epic = (action$) =>
  action$.pipe(
    ofType(refreshTokenSuccess.type, loginSuccess.type),
    pluck('payload'),
    delayWhen(({ ttl = 890 }: AuthenticationResult) => timer((ttl - 10) * 1000)),
    switchMap(() =>
      from(api.refreshToken()).pipe(
        switchMap(({ data }) => of(refreshTokenSuccess(data), getUserConfig())),
        catchError(() => {
          return of(logoutUser());
        }),
      ),
    ),
  );

const verifyTokenEpic: Epic = (actions$) =>
  actions$.pipe(
    ofType(refreshToken.type),
    switchMap(() =>
      from(api.refreshToken()).pipe(
        switchMap(({ data }) => {
          return of(refreshTokenSuccess(data), getUserConfig());
        }),
        catchError(() => {
          return of(logoutUser());
        }),
      ),
    ),
  );

const logoutUserEpic: Epic = (action$) =>
  action$.pipe(
    ofType(logoutUser.type),
    switchMap(() => {
      clearToken();
      return of(logoutUserSuccess());
    }),
  );

const resetPasswordEpic: Epic = (action$, _, { history }) =>
  action$.pipe(
    ofType(resetPassword.type),
    switchMap(({ payload }) =>
      from(api.changePassword(payload)).pipe(
        switchMap(() => {
          history.push('/login');
          notify(i18n.t('successmessage.resetPassword'), 'success');
          return of(resetPasswordSuccess());
        }),
        catchError((error) => {
          apiErrorHandler(error);
          return of(resetPasswordError());
        }),
      ),
    ),
  );

const forgotPasswordEpic: Epic = (action$) =>
  action$.pipe(
    ofType(forgotPassword.type),
    switchMap(({ payload }) =>
      from(api.sendResetPasswordEmail(payload)).pipe(
        switchMap(() => {
          notify(i18n.t('successmessage.forgotPassword'), 'success');
          return of(resetPasswordSuccess());
        }),
        catchError((error) => {
          apiErrorHandler(error);
          return of(resetPasswordError());
        }),
      ),
    ),
  );

const verifyResetPasswordTokenEpic: Epic = (action$, _, { history }) =>
  action$.pipe(
    ofType(verifyResetPasswordToken.type),
    switchMap(({ payload }) =>
      from(api.verifyResetPasswordToken(payload)).pipe(
        switchMap(() => {
          return of(verifyResetPasswordTokenSuccess());
        }),
        catchError((error) => {
          history.push('/login');
          if (error.response?.status === 404) {
            notify(i18n.t('errormessage.resetPasswordToken'), 'error');
          } else {
            apiErrorHandler(error);
          }
          return of(verifyResetPasswordTokenError());
        }),
      ),
    ),
  );

const getKenticoProjectEpic: Epic = (action$) =>
  action$.pipe(
    ofType(getKenticoProject.type),
    switchMap(({ payload }) =>
      from(api.getProjectByKontentProjectId(kenticoSdk?.context?.projectId || '')).pipe(
        switchMap(({ data }) => of(chooseKenticoProject(data), loginSuccess(payload))),
        catchError((error) => {
          apiErrorHandler(error);

          return of(getKenticoProjectError());
        }),
      ),
    ),
  );

const getProjectPrefixEpic: Epic = (action$) =>
  action$.pipe(
    ofType(getProjectPrefix.type),
    switchMap(() =>
      from(api.getProjectPrefixForCurrentUser()).pipe(
        switchMap(({ data }) => {
          return of(getProjectPrefixSuccess(data));
        }),
        catchError((error) => {
          apiErrorHandler(error);
          return of(getProjectPrefixError());
        }),
      ),
    ),
  );

const getUserConfigEpic: Epic = (action$) =>
  action$.pipe(
    ofType(getUserConfig.type),
    switchMap(() =>
      from(api.getAllowDueDateChangeForCurrentUser()).pipe(
        switchMap(({ data }) => {
          return of(getUserConfigSuccess(data));
        }),
        catchError((error) => {
          apiErrorHandler(error);
          return of(getUserConfigError());
        }),
      ),
    ),
  );

export const authEpics = combineEpics(
  loginEpic,
  logoutUserEpic,
  refreshTokenEpic,
  verifyTokenEpic,
  resetPasswordEpic,
  verifyResetPasswordTokenEpic,
  forgotPasswordEpic,
  getKenticoProjectEpic,
  getProjectPrefixEpic,
  getUserConfigEpic,
);
